import {
  I2cCheckbox,
  I2cCheckboxGroup,
  I2cInput,
  I2cListItem,
  I2cRadioButton,
  I2cRadioGroup
} from "@webcomponents/i2cwebcomponents/dist/react";
import "../../PrepaidComponents/Prepaid.scss";
import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup"; 
import ErrorMessage from "../../../Components/SharedComponent/ErrorMessage/ErrorMessage";
import { agentWebsiteLang, hostVirtualCardOptions, technologySpecOptions } from "./stringUtils";
import { handleCheckboxArrayToggle, handleInputChange, handleRadioChange } from "./formUtils";
import { validationSchemaViewVirtualCard } from "./ValidationSchema"; 


const ViewVirtualCard = ({
  setAddFormData,
  addFormData,
  setFormValidStates,
  formDataJson,
  isFetching,
}) => {
  // Initialize React Hook Form with Yup resolver
  const methods = useForm({
    resolver: yupResolver(validationSchemaViewVirtualCard),
    mode: "all",
    reValidateMode: "all",
    shouldUseNativeValidation: false,
    criteriaMode: "all",
    defaultValues: {
      view_virtual_card_would_you_like_i2c_hosted_view_virtual_card: "no",
      view_virtual_card_technology_specification_for_i2c_web_view_integration: [],
      view_virtual_card_technology_specification_for_i2c_web_view_integration_other: "",
      view_virtual_i2c_hosted_web_view_language: [],
      view_virtual_i2c_hosted_web_view_language_other: "",
    },
  });

  const {
    formState: { errors },
  } = methods;

  const fields = [
    "view_virtual_card_would_you_like_i2c_hosted_view_virtual_card",
    "view_virtual_card_technology_specification_for_i2c_web_view_integration",
    "view_virtual_card_technology_specification_for_i2c_web_view_integration_other",
    "view_virtual_i2c_hosted_web_view_language",
    "view_virtual_i2c_hosted_web_view_language_other",
  ];

  // Handle value changes and update parent state
  const handleValueChanges = React.useCallback(() => {
    const values = methods.getValues();
    setAddFormData({
      ...addFormData,
      3: {
        ...addFormData["3"],
        "3_8": { ...values },
      },
    });
  }, [methods, setAddFormData, addFormData]);

  // Load existing form data
  useEffect(() => {
    if (!isFetching && Object.keys(formDataJson ?? {}).length) {
      fields.forEach((field) => {
        const value = formDataJson[3]?.["3_8"]?.[field];
        if (value !== undefined && value !== null) {
          methods.setValue(field, value);
        }
      });
      methods.trigger();
      handleValueChanges();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetching]);

  // Trigger validation when component mounts
  useEffect(() => {
    methods.trigger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update form validation state when errors change
  useEffect(() => {
    const errorsLength = Object.keys(errors)?.length;
    if (errorsLength === 0) {
      setFormValidStates("3_8", true);
    } else {
      setFormValidStates("3_8", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors]);

  const isHostVirtualCardRequired =
    methods.watch("view_virtual_card_would_you_like_i2c_hosted_view_virtual_card") === "yes";

  return (
    <React.Fragment>
      <div className="forms-instance">
        <div i2c-grid="grid gap-md">
          <div i2c-grid="12">
            <div className="section-space">
              <div className="sub-heading mb-16">
                Would you like i2c hosted “View Virtual Card”?
              </div>
              <I2cRadioGroup
                label=""
                value={methods.watch("view_virtual_card_would_you_like_i2c_hosted_view_virtual_card")}
                name="view_virtual_card_would_you_like_i2c_hosted_view_virtual_card"
                onI2cChange={(e) => {
                  const value = e.target?.value || e;

                  handleRadioChange(
                    methods,
                    "view_virtual_card_would_you_like_i2c_hosted_view_virtual_card",
                    value,
                    value !== "yes" ? {
                      view_virtual_card_technology_specification_for_i2c_web_view_integration: [],
                      view_virtual_card_technology_specification_for_i2c_web_view_integration_other: "",
                      view_virtual_i2c_hosted_web_view_language: [],
                      view_virtual_i2c_hosted_web_view_language_other: "",
                    } : {},
                    handleValueChanges
                  );
                }}
              >
                {hostVirtualCardOptions.map((option) => (
                  <I2cRadioButton key={option.value} value={option.value}>
                    {option.label}
                  </I2cRadioButton>
                ))}
              </I2cRadioGroup>
              {errors.view_virtual_card_would_you_like_i2c_hosted_view_virtual_card && (
                <ErrorMessage message={errors.view_virtual_card_would_you_like_i2c_hosted_view_virtual_card.message} />
              )}
            </div>
            {isHostVirtualCardRequired && (
              <div className="section-space">
                <div className="sub-heading mb-16">
                  Technology Specification for i2c Web View Integration
                </div>
                <I2cCheckboxGroup
                  key={`virtual-card-technology-${methods.watch("view_virtual_card_would_you_like_i2c_hosted_view_virtual_card")}`}
                  className="sub-section"
                >
                  <div i2c-grid="grid gap-sm">
                    {technologySpecOptions.map((tech, index) => (
                      <I2cListItem
                        key={index}
                        i2c-grid="4@md"
                        variant="receipt"
                        label={tech.label}
                        value=""
                        type="checkbox"
                        checked={methods.watch("view_virtual_card_technology_specification_for_i2c_web_view_integration").includes(tech.value)}
                        onI2cCheckboxClicked={() => 
                          handleCheckboxArrayToggle(
                            methods,
                            "view_virtual_card_technology_specification_for_i2c_web_view_integration",
                            tech.value,
                            { dependentField: "view_virtual_card_technology_specification_for_i2c_web_view_integration_other" },
                            handleValueChanges
                          )
                        }
                      >
                        <I2cCheckbox
                          slot="prefix"
                          name="view_virtual_card_technology_specification_for_i2c_web_view_integration"
                          value={tech.value}
                          label=""
                          filled
                          checked={methods.watch("view_virtual_card_technology_specification_for_i2c_web_view_integration").includes(tech.value)}
                          className="customCheckbox"
                        ></I2cCheckbox>
                      </I2cListItem>
                    ))}
                  </div>
                </I2cCheckboxGroup>
                {errors.view_virtual_card_technology_specification_for_i2c_web_view_integration && (
                  <ErrorMessage message={errors.view_virtual_card_technology_specification_for_i2c_web_view_integration.message} />
                )}
                {methods.watch("view_virtual_card_technology_specification_for_i2c_web_view_integration").includes("other") && (
                  <div i2c-grid="grid 4@md">
                    <div className="single-field-wrapper">
                      <I2cInput
                        className="single-input-field"
                        placeholder="Please Specify"
                        size="large"
                        label="Enter Details"
                        name="view_virtual_card_technology_specification_for_i2c_web_view_integration_other"
                        value={methods.watch("view_virtual_card_technology_specification_for_i2c_web_view_integration_other")}
                        onI2cInput={(e) =>
                          handleInputChange(methods, "view_virtual_card_technology_specification_for_i2c_web_view_integration_other", e.target.value, handleValueChanges)
                        }
                        invalid={errors.view_virtual_card_technology_specification_for_i2c_web_view_integration_other ? true : false}
                      />
                      {errors.view_virtual_card_technology_specification_for_i2c_web_view_integration_other && (
                        <ErrorMessage message={errors.view_virtual_card_technology_specification_for_i2c_web_view_integration_other.message} />
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
            {isHostVirtualCardRequired && (
              <div className="section-space">
                  <div className="sub-heading mb-16">
                    i2c Hosted Web View Language
                  </div>
                  <I2cCheckboxGroup
                    key={`virtual-card-language-${methods.watch("view_virtual_card_would_you_like_i2c_hosted_view_virtual_card")}`}
                  >
                    <div i2c-grid="grid gap-sm">
                      {agentWebsiteLang.map((web, index) => (
                        <I2cListItem
                          key={index}
                          i2c-grid="4@md"
                          variant="receipt"
                          label={web.label}
                          value=""
                          type="checkbox"
                          checked={methods.watch("view_virtual_i2c_hosted_web_view_language").includes(web.value)}
                          onI2cCheckboxClicked={() =>
                            handleCheckboxArrayToggle(
                              methods,
                              "view_virtual_i2c_hosted_web_view_language",
                              web.value,
                              { dependentField: "view_virtual_i2c_hosted_web_view_language_other" },
                              handleValueChanges
                            )
                          }
                        >
                          <I2cCheckbox
                            slot="prefix"
                            name="view_virtual_i2c_hosted_web_view_language"
                            value={web.value}
                            label=""
                            filled
                            checked={methods.watch("view_virtual_i2c_hosted_web_view_language").includes(web.value)}
                            className="customCheckbox"
                          ></I2cCheckbox>
                        </I2cListItem>
                      ))}
                    </div>
                  </I2cCheckboxGroup>
                  {errors.view_virtual_i2c_hosted_web_view_language && (
                    <ErrorMessage message={errors.view_virtual_i2c_hosted_web_view_language.message} />
                  )}
                  {methods.watch("view_virtual_i2c_hosted_web_view_language").includes("other") && (
                    <div i2c-grid="grid 4@md" className="mt-16">
                      <div className="single-field-wrapper">
                        <I2cInput
                          className="single-input-field"
                          placeholder="Please Specify"
                          size="large"
                          label="Enter Details"
                          name="view_virtual_i2c_hosted_web_view_language_other"
                          value={methods.watch("view_virtual_i2c_hosted_web_view_language_other")}
                          onI2cInput={(e) =>
                            handleInputChange(methods, "view_virtual_i2c_hosted_web_view_language_other", e.target.value, handleValueChanges)
                          }
                          invalid={errors.view_virtual_i2c_hosted_web_view_language_other ? true : false}
                        />
                        {errors.view_virtual_i2c_hosted_web_view_language_other && (
                          <ErrorMessage message={errors.view_virtual_i2c_hosted_web_view_language_other.message} />
                        )}
                      </div>
                    </div>
                  )}
                </div>
            )}
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default ViewVirtualCard;
